// Authed review-tool writes — POST comment + annotation
// (PRD §9.7 + B4 PR5b / B4e2).
//
// Mirrors `usePostShareComment` / `usePostShareAnnotation` from
// mutations/share.ts. Same optimistic-insert + rollback shape:
// onMutate prepends a synthetic row with a `tmp-…` id; onError
// restores the snapshot; onSuccess swaps the placeholder for the
// server-canonical row in place so the rendered order doesn't shift.
//
// Authed surface differences from the share variant:
//   - No `token`, no `password` query — the project guard covers
//     access; the JWT/PAT on Authorization handles identity.
//   - `author_display_name` is never on the wire — the backend
//     stamps the comment from the JWT's user (the share endpoint
//     accepts the field for anonymous-allowed links).

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { client } from '../client';
import {
  reviewKeys,
  type AnnotationOut,
  type CommentOut,
} from '../queries/review';
import type { components } from '../schema';

type CommentCreate = components['schemas']['CommentCreate'];
type AnnotationCreate = components['schemas']['AnnotationCreate'];
type StrokePrimitive = components['schemas']['StrokePrimitive'];

export interface UsePostCommentVars {
  body: string;
  frame?: number | null;
  timecode_seconds?: number | null;
  parent_id?: string | null;
  annotation_id?: string | null;
}

interface PostCommentContext {
  previous: CommentOut[] | undefined;
  optimisticId: string;
}

async function postComment(
  orgSlug: string,
  projectId: string,
  candidateId: string,
  vars: UsePostCommentVars
): Promise<CommentOut> {
  const body: CommentCreate = {
    body: vars.body,
    frame: vars.frame ?? null,
    timecode_seconds: vars.timecode_seconds ?? null,
    parent_id: vars.parent_id ?? null,
    annotation_id: vars.annotation_id ?? null,
    // The backend ignores `author_display_name` on the authed route
    // (it's stamped from the JWT) but the schema accepts it. Pass null
    // so the body matches the type without sending a wire field that
    // would be discarded.
    author_display_name: null,
  };
  const { data } = await client.POST(
    '/api/v1/orgs/{org_slug}/projects/{project_id}/candidates/{candidate_id}/comments',
    {
      params: {
        path: {
          org_slug: orgSlug,
          project_id: projectId,
          candidate_id: candidateId,
        },
      },
      body,
    }
  );
  if (!data) throw new Error('usePostComment: empty response');
  return data;
}

function tempId(prefix: string): string {
  return `tmp-${prefix}-${Date.now().toString()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function usePostComment(
  orgSlug: string,
  projectId: string,
  candidateId: string
): UseMutationResult<
  CommentOut,
  Error,
  UsePostCommentVars,
  PostCommentContext
> {
  const queryClient = useQueryClient();
  const key = reviewKeys.comments(orgSlug, projectId, candidateId);
  return useMutation<
    CommentOut,
    Error,
    UsePostCommentVars,
    PostCommentContext
  >({
    mutationFn: (vars) => postComment(orgSlug, projectId, candidateId, vars),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CommentOut[]>(key);
      const optimisticId = tempId('comment');
      const now = new Date().toISOString();
      const optimistic: CommentOut = {
        id: optimisticId,
        created_at: now,
        updated_at: now,
        candidate_id: candidateId,
        author_id: null,
        author_display_name: 'You',
        body: vars.body,
        frame: vars.frame ?? null,
        timecode_seconds: vars.timecode_seconds ?? null,
        parent_id: vars.parent_id ?? null,
        thread_root_id: null,
        annotation_id: vars.annotation_id ?? null,
        is_resolved: false,
        resolved_at: null,
        resolved_by_id: null,
        source: null,
      };
      queryClient.setQueryData<CommentOut[]>(key, (prev) =>
        prev ? [...prev, optimistic] : [optimistic]
      );
      return { previous, optimisticId };
    },
    onError: (_err, _vars, context) => {
      if (context !== undefined) {
        queryClient.setQueryData<CommentOut[]>(key, context.previous);
      }
    },
    onSuccess: (saved, _vars, context) => {
      queryClient.setQueryData<CommentOut[]>(key, (prev) => {
        if (!prev) return [saved];
        const idx = prev.findIndex((c) => c.id === context.optimisticId);
        if (idx === -1) return [...prev, saved];
        const next = prev.slice();
        next[idx] = saved;
        return next;
      });
    },
  });
}

export interface UsePostAnnotationVars {
  frame: number;
  strokes_json: StrokePrimitive[];
  timecode_seconds?: number | null;
}

interface PostAnnotationContext {
  previous: AnnotationOut[] | undefined;
  optimisticId: string;
}

async function postAnnotation(
  orgSlug: string,
  projectId: string,
  candidateId: string,
  vars: UsePostAnnotationVars
): Promise<AnnotationOut> {
  const body: AnnotationCreate = {
    frame: vars.frame,
    strokes_json: vars.strokes_json,
    timecode_seconds: vars.timecode_seconds ?? null,
    author_display_name: null,
  };
  const { data } = await client.POST(
    '/api/v1/orgs/{org_slug}/projects/{project_id}/candidates/{candidate_id}/annotations',
    {
      params: {
        path: {
          org_slug: orgSlug,
          project_id: projectId,
          candidate_id: candidateId,
        },
      },
      body,
    }
  );
  if (!data) throw new Error('usePostAnnotation: empty response');
  return data;
}

export function usePostAnnotation(
  orgSlug: string,
  projectId: string,
  candidateId: string
): UseMutationResult<
  AnnotationOut,
  Error,
  UsePostAnnotationVars,
  PostAnnotationContext
> {
  const queryClient = useQueryClient();
  return useMutation<
    AnnotationOut,
    Error,
    UsePostAnnotationVars,
    PostAnnotationContext
  >({
    mutationFn: (vars) =>
      postAnnotation(orgSlug, projectId, candidateId, vars),
    onMutate: async (vars) => {
      const key = reviewKeys.annotations(
        orgSlug,
        projectId,
        candidateId,
        vars.frame
      );
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AnnotationOut[]>(key);
      const optimisticId = tempId('annotation');
      const now = new Date().toISOString();
      const optimistic: AnnotationOut = {
        id: optimisticId,
        created_at: now,
        updated_at: now,
        candidate_id: candidateId,
        author_id: null,
        author_display_name: 'You',
        frame: vars.frame,
        timecode_seconds: vars.timecode_seconds ?? null,
        strokes_json: vars.strokes_json,
        snapshot_s3_key: null,
        review_session_id: null,
      };
      queryClient.setQueryData<AnnotationOut[]>(key, (prev) =>
        prev ? [...prev, optimistic] : [optimistic]
      );
      return { previous, optimisticId };
    },
    onError: (_err, vars, context) => {
      if (context === undefined) return;
      const key = reviewKeys.annotations(
        orgSlug,
        projectId,
        candidateId,
        vars.frame
      );
      queryClient.setQueryData<AnnotationOut[]>(key, context.previous);
    },
    onSuccess: (saved, vars, context) => {
      const key = reviewKeys.annotations(
        orgSlug,
        projectId,
        candidateId,
        vars.frame
      );
      queryClient.setQueryData<AnnotationOut[]>(key, (prev) => {
        if (!prev) return [saved];
        const idx = prev.findIndex((a) => a.id === context.optimisticId);
        if (idx === -1) return [...prev, saved];
        const next = prev.slice();
        next[idx] = saved;
        return next;
      });
    },
  });
}
